/** Detalle de presupuesto: lectura + acciones + partidas + edición comercial + versiones. */
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { MobileLayout } from '@/components/MobileLayout';
import { MobileButton } from '@/components/MobileButton';
import { MobileIcon } from '@/components/MobileIcon';
import { MobileInput } from '@/components/MobileInput';
import { FileCapture } from '@/components/FileCapture';
import { QuantityStepper } from '@/components/QuantityStepper';
import { SectionCard } from '@/components/SectionCard';
import { StatusBadge } from '@/components/StatusBadge';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ServerActionRunner } from '@/components/ServerActionRunner';
import { CardListSkeleton } from '@/components/LoadingSkeleton';
import { EmptyState } from '@/components/EmptyState';
import {
  useAddBudgetItemMutation,
  useBudgetActionMutation,
  useBudgetAdjustmentMutations,
  useBudgetTotalsMutations,
  useDeleteBudgetItemMutation,
  useGenerateQuotationMutation,
  useMobileBudgetAttachmentsQuery,
  useMobileBudgetContextQuery,
  useMobileBudgetItemsQuery,
  useMobileBudgetQuery,
  useMobileBudgetSnapshotsQuery,
  useMobileCatalogQuery,
  useUpdateBudgetItemMutation,
  useUploadBudgetAttachmentsMutation,
} from '@/hooks/useMobileQuotations';
import { useMobileAuth } from '@/state/MobileAuthContext';
import type { MobileBudgetItemDto } from '@/services/commercialService';
import { extractErrorMessage } from '@/services/mobileApiClient';
import { formatCurrency } from '@/utils/format';
import { hapticSuccess } from '@/utils/haptic';

/** Acciones de flujo del presupuesto visibles en el móvil (approve/reject van por tarea). */
const MOBILE_BUDGET_ACTIONS = ['send_to_commercial', 'request_workflow', 'cancel'];

/** Misma regla que la web: lo congela la decisión (approved), no la etapa. */
function isBudgetEditable(status: string | undefined): boolean {
  return status !== 'approved' && status !== 'cancelled' && status !== 'superseded';
}

function toNumber(value: number | string | undefined): number | undefined {

  if (value === null || value === undefined) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function formatDate(value: string | undefined): string {
  if (!value) return '—';
  const date = new Date(value.length <= 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function lineKindLabel(lineKind: string): string {
  return lineKind === 'commercial' ? 'Comercial' : 'Técnica';
}

function itemTitle(item: MobileBudgetItemDto): string {
  return item.description || item.master_item?.name || 'Partida';
}

type BuildResult = { ok: false; error: string } | { ok: true; payload: Record<string, unknown> };

export function BudgetDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = useMobileAuth();
  const budgetId = id ?? null;
  const budgetQuery = useMobileBudgetQuery(budgetId);
  const contextQuery = useMobileBudgetContextQuery(budgetId);
  const itemsQuery = useMobileBudgetItemsQuery(budgetId);
  const snapshotsQuery = useMobileBudgetSnapshotsQuery(budgetId);
  const attachmentsQuery = useMobileBudgetAttachmentsQuery(budgetId);
  const uploadAttachmentsMutation = useUploadBudgetAttachmentsMutation();
  const actionMutation = useBudgetActionMutation(budgetId ?? '');
  const generateMutation = useGenerateQuotationMutation(budgetId ?? '');
  const addItemMutation = useAddBudgetItemMutation(budgetId ?? '');
  const updateItemMutation = useUpdateBudgetItemMutation(budgetId ?? '');
  const deleteItemMutation = useDeleteBudgetItemMutation(budgetId ?? '');
  const adjustments = useBudgetAdjustmentMutations(budgetId ?? '');
  const totals = useBudgetTotalsMutations(budgetId ?? '');

  const [addSearch, setAddSearch] = useState('');
  const [selectedCatalogId, setSelectedCatalogId] = useState<string | null>(null);
  const [addQuantity, setAddQuantity] = useState(1);
  const [addPrice, setAddPrice] = useState('');
  const [addMargin, setAddMargin] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editQuantity, setEditQuantity] = useState('1');
  const [editPrice, setEditPrice] = useState('0');
  const [editMargin, setEditMargin] = useState('');
  const [deletingItem, setDeletingItem] = useState<MobileBudgetItemDto | null>(null);
  const [adjustmentType, setAdjustmentType] = useState<'discount' | 'surcharge'>('discount');
  const [adjustmentMode, setAdjustmentMode] = useState<'amount' | 'percent'>('amount');
  const [adjustmentValue, setAdjustmentValue] = useState('');
  const [adjustmentJustification, setAdjustmentJustification] = useState('');
  const [adjustmentError, setAdjustmentError] = useState<string | null>(null);
  const [simulationPreview, setSimulationPreview] = useState<string | null>(null);
  const [clearingItem, setClearingItem] = useState<MobileBudgetItemDto | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const catalogQuery = useMobileCatalogQuery(addSearch, 'additional');

  if (budgetQuery.isPending) {
    return (
      <MobileLayout title="Presupuesto" showBack>
        <CardListSkeleton count={3} />
      </MobileLayout>
    );
  }

  if (budgetQuery.isError || !budgetQuery.data) {
    return (
      <MobileLayout title="Presupuesto" showBack>
        <EmptyState
          icon="error"
          title="No se pudo cargar"
          description="Verifica tu conexión e intenta de nuevo."
          actionLabel="Reintentar"
          onAction={() => void budgetQuery.refetch()}
        />
      </MobileLayout>
    );
  }

  const budget = budgetQuery.data;
  const currency = budget.currency ?? 'USD';
  const actions = contextQuery.data?.actions ?? [];
  const documentRevision = contextQuery.data?.document_revision;
  const clientName = budget.customer_requirement?.client?.legal_name;
  const requirementCode = budget.customer_requirement?.code;
  const items = itemsQuery.data ?? [];
  const snapshots = snapshotsQuery.data ?? [];
  const attachments = attachmentsQuery.data ?? [];
  const editable = isBudgetEditable(budget.status);
  const canViewSnapshots = hasPermission('budgets.view_snapshots');
  const canGenerateQuotation = budget.status === 'approved' && hasPermission('quotations.create');
  const canAddCommercial = editable && hasPermission('budgets.commercial_items.create');
  const canEditCommercial = editable && hasPermission('budgets.commercial_items.update');
  const canDeleteCommercial = editable && hasPermission('budgets.commercial_items.delete');
  const canAdjust = editable && hasPermission('budgets.commercial_items.create');
  const canSearchCatalog = hasPermission('quotations.create');

  const commercialItems = items.filter((item) => item.line_kind === 'commercial');
  const technicalItems = items.filter((item) => item.line_kind !== 'commercial');
  const adjustmentItems = items.filter((item) => item.adjustment_type);
  const plainCommercial = commercialItems.filter((item) => !item.adjustment_type);

  async function handleBudgetAction(actionCode: string, body: { reason?: string }, revision?: number) {
    return actionMutation.mutateAsync({ action: actionCode, body, revision });
  }

  function handleBudgetActionSuccess() {
    hapticSuccess();
    toast.success('Acción ejecutada correctamente');
    void budgetQuery.refetch();
    void contextQuery.refetch();
  }

  async function handleFiles(files: File[]) {
    if (!budgetId || files.length === 0) return;
    setUploadError(null);
    try {
      await uploadAttachmentsMutation.mutateAsync({ budgetId, files });
      hapticSuccess();
      toast.success(files.length === 1 ? 'Adjunto subido correctamente' : 'Adjuntos subidos correctamente');
    } catch (error) {
      setUploadError(extractErrorMessage(error));
    }
  }

  async function handleGenerateQuotation() {
    try {
      const quotation = await generateMutation.mutateAsync();
      hapticSuccess();
      toast.success(`Cotización ${quotation.quotation_number} creada`);
      void contextQuery.refetch();
    } catch (error) {
      toast.error(extractErrorMessage(error));
    }
  }

  function buildAddPayload(): BuildResult {
    const selected = (catalogQuery.data ?? []).find((entry) => entry.id === selectedCatalogId);
    if (!selected) return { ok: false, error: 'Elige un artículo del catálogo' };
    if (!Number.isFinite(addQuantity) || addQuantity <= 0) {
      return { ok: false, error: 'Ingresa una cantidad mayor que 0' };
    }
    const priceValue = addPrice.trim() === '' ? Number(selected.price ?? 0) || 0 : Number(addPrice);
    if (!Number.isFinite(priceValue) || priceValue < 0) {
      return { ok: false, error: 'Ingresa un precio válido (0 o mayor)' };
    }
    const marginValue = addMargin.trim() === '' ? null : Number(addMargin);
    if (marginValue !== null && (!Number.isFinite(marginValue) || marginValue < 0 || marginValue > 100)) {
      return { ok: false, error: 'Ingresa un margen válido (0 a 100)' };
    }
    return {
      ok: true,
      payload: {
        master_item_id: selected.id,
        item_type: selected.item_type ?? 'sales',
        line_kind: 'commercial',
        quantity: addQuantity,
        master_item_category_id: null,
        exchange_rate: Number(budget.exchange_rate) || 0,
        ...(marginValue !== null ? { margin: marginValue } : {}),
        ...(selected.item_type === 'component'
          ? { base_price: 0, children: [] as unknown[] }
          : { unit_cost: priceValue, base_price: priceValue }),
      },
    };
  }

  async function handleAddItem() {
    const built = buildAddPayload();
    if (!built.ok) {
      setAddError(built.error);
      return;
    }
    setAddError(null);
    try {
      await addItemMutation.mutateAsync({ body: built.payload, revision: documentRevision });
      setSelectedCatalogId(null);
      setAddSearch('');
      setAddQuantity(1);
      setAddPrice('');
      setAddMargin('');
      hapticSuccess();
      toast.success('Artículo agregado');
    } catch (error) {
      setAddError(extractErrorMessage(error));
    }
  }

  function startEditing(item: MobileBudgetItemDto) {
    setEditingItemId(item.id);
    setEditQuantity(String(Number(item.quantity ?? 1)));
    setEditPrice(String(Number(item.unit_cost ?? item.base_price ?? 0)));
    setEditMargin(item.margin === null || item.margin === undefined ? '' : String(item.margin));
  }

  async function handleSaveEditing(item: MobileBudgetItemDto) {
    const quantityValue = Number(editQuantity);
    const priceValue = Number(editPrice);
    const marginValue = editMargin.trim() === '' ? null : Number(editMargin);
    if (!Number.isFinite(quantityValue) || quantityValue <= 0) {
      toast.error('Ingresa una cantidad mayor que 0');
      return;
    }
    if (!Number.isFinite(priceValue) || priceValue < 0) {
      toast.error('Ingresa un precio válido (0 o mayor)');
      return;
    }
    if (marginValue !== null && (!Number.isFinite(marginValue) || marginValue < 0 || marginValue > 100)) {
      toast.error('Ingresa un margen válido (0 a 100)');
      return;
    }
    try {
      const patches: Record<string, unknown>[] = [];
      if (quantityValue !== Number(item.quantity)) patches.push({ quantity: quantityValue });
      if (priceValue !== Number(item.unit_cost ?? item.base_price ?? 0)) patches.push({ unit_cost: priceValue });
      const currentMargin = item.margin === null || item.margin === undefined ? null : Number(item.margin);
      if (marginValue !== currentMargin) patches.push({ margin: marginValue });
      for (const patch of patches) {
        await updateItemMutation.mutateAsync({ itemId: item.id, body: patch, revision: documentRevision });
      }
      setEditingItemId(null);
      hapticSuccess();
      toast.success(patches.length > 0 ? 'Partida actualizada' : 'Sin cambios');
    } catch (error) {
      toast.error(extractErrorMessage(error));
    }
  }

  async function handleConfirmDelete() {
    if (!deletingItem) return;
    try {
      await deleteItemMutation.mutateAsync({ itemId: deletingItem.id, revision: documentRevision });
      setDeletingItem(null);
      hapticSuccess();
      toast.success('Partida eliminada');
    } catch (error) {
      toast.error(extractErrorMessage(error));
    }
  }

  function buildAdjustmentPayload(): BuildResult {
    const parsed = Number(adjustmentValue);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return { ok: false, error: 'El valor debe ser un número mayor a 0' };
    }
    if (adjustmentMode === 'percent' && parsed > 100) {
      return { ok: false, error: 'El porcentaje no puede superar el 100%' };
    }
    if (!adjustmentJustification || adjustmentJustification.trim().length < 5) {
      return { ok: false, error: 'La justificación es obligatoria (mínimo 5 caracteres)' };
    }
    return {
      ok: true,
      payload: {
        adjustment_type: adjustmentType,
        ...(adjustmentMode === 'percent' ? { percent: parsed } : { amount: parsed }),
        justification: adjustmentJustification.trim(),
      },
    };
  }

  async function handleSimulateAdjustment() {
    const built = buildAdjustmentPayload();
    if (!built.ok) {
      setAdjustmentError(built.error);
      return;
    }
    setAdjustmentError(null);
    setSimulationPreview(null);
    try {
      const result = (await totals.simulate.mutateAsync(built.payload)) as {
        total_sale_price?: number | string;
        sale_price?: number | string;
      };
      const previewTotal = result.total_sale_price ?? result.sale_price;
      setSimulationPreview(
        previewTotal === undefined
          ? 'Simulación calculada sin total de referencia.'
          : `Total simulado: ${formatCurrency(Number(previewTotal), currency)}`,
      );
    } catch (error) {
      setAdjustmentError(extractErrorMessage(error));
    }
  }

  async function handleApplyAdjustment() {
    const built = buildAdjustmentPayload();
    if (!built.ok) {
      setAdjustmentError(built.error);
      return;
    }
    setAdjustmentError(null);
    try {
      await adjustments.applyAdjustment.mutateAsync({ body: built.payload, revision: documentRevision });
      setAdjustmentValue('');
      setAdjustmentJustification('');
      setSimulationPreview(null);
      hapticSuccess();
      toast.success('Ajuste aplicado al precio de venta');
    } catch (error) {
      setAdjustmentError(extractErrorMessage(error));
    }
  }

  async function handleConfirmClearAdjustment() {
    if (!clearingItem) return;
    try {
      await adjustments.clearAdjustment.mutateAsync({ itemId: clearingItem.id, revision: documentRevision });
      setClearingItem(null);
      hapticSuccess();
      toast.success('Ajuste eliminado');
    } catch (error) {
      toast.error(extractErrorMessage(error));
    }
  }

  return (
    <MobileLayout
      title={`${budget.budget_code} · v${budget.version_number}`}
      subtitle={clientName ?? 'Presupuesto'}
      showBack
    >
      <div className="space-y-4">
        <SectionCard
          title="Datos generales"
          action={<StatusBadge status={budget.status} size="sm" />}
        >
          <dl className="divide-y divide-border-light text-sm">
            <div className="flex items-center justify-between gap-3 py-2">
              <dt className="shrink-0 text-text-tertiary">Codigo</dt>
              <dd title={budget.budget_code} className="min-w-0 flex-1 truncate text-right font-semibold text-text-primary">
                {budget.budget_code}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3 py-2">
              <dt className="shrink-0 text-text-tertiary">Version</dt>
              <dd className="min-w-0 flex-1 truncate text-right tabular-nums text-text-secondary">
                v{budget.version_number}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3 py-2">
              <dt className="shrink-0 text-text-tertiary">Cliente</dt>
              <dd className="min-w-0 flex-1 break-words text-right font-medium text-text-primary">
                {clientName ?? '—'}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3 py-2">
              <dt className="shrink-0 text-text-tertiary">Requerimiento</dt>
              <dd title={requirementCode ?? undefined} className="min-w-0 flex-1 truncate text-right text-text-secondary">
                {requirementCode ?? '—'}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3 py-2">
              <dt className="shrink-0 text-text-tertiary">Fecha</dt>
              <dd className="min-w-0 flex-1 truncate text-right tabular-nums text-text-secondary">
                {formatDate(budget.budget_date)}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3 py-2">
              <dt className="shrink-0 text-text-tertiary">Moneda</dt>
              <dd className="min-w-0 flex-1 truncate text-right text-text-secondary">
                {currency}
              </dd>
            </div>
          </dl>
        </SectionCard>

        <SectionCard title="Importes">
          <dl className="divide-y divide-border-light text-sm">
            <div className="flex items-center justify-between gap-3 py-2">
              <dt className="shrink-0 text-text-tertiary">Subtotal</dt>
              <dd className="min-w-0 flex-1 truncate text-right font-medium tabular-nums text-text-primary">
                {formatCurrency(toNumber(budget.subtotal), currency)}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3 py-2">
              <dt className="shrink-0 text-text-tertiary">Ajuste</dt>
              <dd className="min-w-0 flex-1 truncate text-right tabular-nums text-text-secondary">
                {formatCurrency(toNumber(budget.adjustment_amount), currency)}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3 py-2">
              <dt className="shrink-0 text-text-tertiary">Costo total</dt>
              <dd className="min-w-0 flex-1 truncate text-right tabular-nums text-text-secondary">
                {formatCurrency(toNumber(budget.total_cost), currency)}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3 py-2">
              <dt className="shrink-0 font-semibold text-text-primary">Precio de venta</dt>
              <dd className="min-w-0 flex-1 truncate text-right text-base font-bold tabular-nums text-primary">
                {formatCurrency(toNumber(budget.sale_price), currency)}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3 py-2">
              <dt className="shrink-0 text-text-tertiary">Margen est.</dt>
              <dd className="min-w-0 flex-1 truncate text-right tabular-nums text-text-secondary">
                {formatCurrency(toNumber(budget.estimated_margin), currency)}
              </dd>
            </div>
          </dl>
        </SectionCard>

        {(actions.length > 0 || canGenerateQuotation) && (
          <SectionCard
            title="Acciones"
            subtitle="El servidor habilito estas acciones para este presupuesto."
          >
            <div className="space-y-2">
              <ServerActionRunner
                actions={actions}
                documentRevision={documentRevision}
                allowed={MOBILE_BUDGET_ACTIONS}
                onExecute={handleBudgetAction}
                onSuccess={handleBudgetActionSuccess}
                onConflictReload={() => {
                  void budgetQuery.refetch();
                  void contextQuery.refetch();
                }}
              />
              {canGenerateQuotation && (
                <MobileButton
                  variant="primary"
                  size="md"
                  fullWidth
                  loading={generateMutation.isPending}
                  icon={<MobileIcon name="request_quote" className="h-5 w-5" />}
                  onClick={() => void handleGenerateQuotation()}
                >
                  Generar cotización
                </MobileButton>
              )}
            </div>
          </SectionCard>
        )}

        <SectionCard
          title="Partidas"
          subtitle="Lectura de ambos lados; los costos los enmascara el servidor."
        >
          {itemsQuery.isPending && <CardListSkeleton count={3} />}
          {itemsQuery.isError && (
            <EmptyState
              icon="error"
              title="No se pudieron cargar las partidas"
              description="Verifica tu conexión e intenta de nuevo."
              actionLabel="Reintentar"
              onAction={() => void itemsQuery.refetch()}
            />
          )}
          {!itemsQuery.isPending && !itemsQuery.isError && items.length === 0 && (
            <div className="rounded-xl bg-bg-tertiary p-4 text-center">
              <p className="truncate text-sm font-semibold text-text-primary">Sin partidas</p>
              <p className="text-sm text-text-secondary">Este presupuesto aún no tiene partidas.</p>
            </div>
          )}
          {!itemsQuery.isPending &&
            !itemsQuery.isError &&
            items.length > 0 &&
            (
              [
                { kind: 'technical', rows: technicalItems },
                { kind: 'commercial', rows: commercialItems },
              ] as const
            ).map(
              (group) =>
                group.rows.length > 0 && (
                  <div key={group.kind} className="mb-3 last:mb-0">
                    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-tertiary">
                      Lado {lineKindLabel(group.kind)} ({group.rows.length})
                    </h3>
                    <ul className="space-y-2">
                      {group.rows.map((item) => (
                        <li
                          key={item.id}
                          className="flex min-h-[52px] items-center gap-2 rounded-xl bg-bg-tertiary px-4 py-2"
                        >
                          <MobileIcon
                            name={group.kind === 'commercial' ? 'store' : 'inventory_2'}
                            className="h-5 w-5 shrink-0 text-text-tertiary"
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block break-words text-sm font-semibold text-text-primary">
                              {itemTitle(item)}
                              {item.adjustment_type ? ` · Ajuste ${item.adjustment_type}` : ''}
                            </span>
                            <span className="block truncate text-xs tabular-nums text-text-secondary">
                              Cant. {String(item.quantity)}
                              {item.unit_sale_price !== undefined && item.unit_sale_price !== null
                                ? ` · ${formatCurrency(Number(item.unit_sale_price), currency)}`
                                : ''}
                            </span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ),
            )}
        </SectionCard>

        {(canAddCommercial || canEditCommercial || canDeleteCommercial || canAdjust) && (
          <SectionCard
            title="Edición comercial"
            subtitle={
              editable
                ? 'Agregar, editar y eliminar partidas de venta.'
                : 'El presupuesto ya no admite cambios.'
            }
          >
            {canAddCommercial && (
              <div className="mb-4 space-y-3 border-b border-border-light pb-4">
                <h3 className="text-sm font-semibold text-text-primary">Agregar artículo</h3>
                {canSearchCatalog ? (
                  <>
                    <MobileInput
                      label="Buscar en catálogo"
                      placeholder="Mínimo 2 letras..."
                      staticLabel
                      value={addSearch}
                      onChange={(e) => {
                        setAddSearch(e.target.value);
                        setSelectedCatalogId(null);
                      }}
                      icon={<MobileIcon name="search" className="h-5 w-5" />}
                    />
                    {addSearch.trim().length >= 2 && (
                      <div className="space-y-2">
                        {catalogQuery.isPending && <CardListSkeleton count={2} />}
                        {catalogQuery.isSuccess && (catalogQuery.data ?? []).length === 0 && (
                          <p className="text-sm text-text-secondary">Sin resultados para esa búsqueda.</p>
                        )}
                        {(catalogQuery.data ?? []).slice(0, 10).map((entry) => (
                          <button
                            key={entry.id}
                            type="button"
                            aria-pressed={selectedCatalogId === entry.id}
                            onClick={() => {
                              setSelectedCatalogId(entry.id);
                              setAddPrice(
                                entry.price !== undefined ? String(entry.price) : '',
                              );
                            }}
                            className={`flex min-h-[52px] w-full items-center gap-2 rounded-xl px-4 py-2 text-left ${
                              selectedCatalogId === entry.id
                                ? 'bg-primary-light ring-2 ring-primary'
                                : 'bg-bg-tertiary'
                            }`}
                          >
                            <MobileIcon name="store" className="h-5 w-5 shrink-0 text-text-tertiary" />
                            <span className="min-w-0 flex-1">
                              <span className="block break-words text-sm font-semibold text-text-primary">
                                {entry.name}
                              </span>
                              <span title={entry.code} className="block truncate text-xs text-text-secondary">
                                {entry.code}
                                {entry.price !== undefined
                                  ? ` · ${formatCurrency(entry.price, entry.currency_code ?? currency)}`
                                  : ''}
                              </span>
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                    {selectedCatalogId && (
                      <div className="space-y-3 rounded-xl bg-bg-tertiary p-3">
                        <QuantityStepper
                          label="cantidad del artículo"
                          value={addQuantity}
                          onChange={setAddQuantity}
                        />
                        <MobileInput
                          label="Precio base"
                          type="number"
                          inputMode="decimal"
                          staticLabel
                          value={addPrice}
                          onChange={(e) => setAddPrice(e.target.value)}
                          icon={<MobileIcon name="payments" className="h-5 w-5" />}
                        />
                        <MobileInput
                          label="Margen % (opcional)"
                          type="number"
                          inputMode="decimal"
                          staticLabel
                          value={addMargin}
                          onChange={(e) => setAddMargin(e.target.value)}
                          icon={<MobileIcon name="trending_up" className="h-5 w-5" />}
                        />
                      </div>
                    )}
                    {addError && <p className="break-words text-sm text-error">{addError}</p>}
                    <MobileButton
                      variant="secondary"
                      size="md"
                      fullWidth
                      loading={addItemMutation.isPending}
                      icon={<MobileIcon name="add" className="h-5 w-5" />}
                      onClick={() => void handleAddItem()}
                    >
                      Agregar artículo
                    </MobileButton>
                  </>
                ) : (
                  <p className="text-sm text-text-secondary">
                    El catálogo exige el permiso de cotizar; el alta de artículos no está disponible.
                  </p>
                )}
              </div>
            )}

            {plainCommercial.length > 0 && (
              <div className="mb-4 space-y-2 border-b border-border-light pb-4 last:mb-0 last:border-0 last:pb-0">
                <h3 className="text-sm font-semibold text-text-primary">
                  Partidas de venta ({plainCommercial.length})
                </h3>
                <ul className="space-y-2">
                  {plainCommercial.map((item) => (
                    <li key={item.id} className="rounded-xl bg-bg-tertiary px-4 py-3">
                      {editingItemId === item.id ? (
                        <div className="space-y-3">
                          <QuantityStepper
                            label={itemTitle(item)}
                            value={Number(editQuantity) || 0}
                            onChange={(next) => setEditQuantity(String(next))}
                          />
                          <MobileInput
                            label="Precio base"
                            type="number"
                            inputMode="decimal"
                            staticLabel
                            value={editPrice}
                            onChange={(e) => setEditPrice(e.target.value)}
                            icon={<MobileIcon name="payments" className="h-5 w-5" />}
                          />
                          <MobileInput
                            label="Margen % (opcional)"
                            type="number"
                            inputMode="decimal"
                            staticLabel
                            value={editMargin}
                            onChange={(e) => setEditMargin(e.target.value)}
                            icon={<MobileIcon name="trending_up" className="h-5 w-5" />}
                          />
                          <div className="grid grid-cols-2 gap-2">
                            <MobileButton variant="ghost" size="sm" onClick={() => setEditingItemId(null)}>
                              Cancelar
                            </MobileButton>
                            <MobileButton
                              variant="primary"
                              size="sm"
                              loading={updateItemMutation.isPending}
                              onClick={() => void handleSaveEditing(item)}
                            >
                              Guardar
                            </MobileButton>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="min-w-0 flex-1">
                            <span className="block break-words text-sm font-semibold text-text-primary">
                              {itemTitle(item)}
                            </span>
                            <span className="block truncate text-xs tabular-nums text-text-secondary">
                              Cant. {String(item.quantity)}
                              {item.unit_sale_price !== undefined && item.unit_sale_price !== null
                                ? ` · ${formatCurrency(Number(item.unit_sale_price), currency)}`
                                : ''}
                            </span>
                          </span>
                          {canEditCommercial && (
                            <button
                              type="button"
                              aria-label={`Editar partida ${itemTitle(item)}`}
                              onClick={() => startEditing(item)}
                              className="flex h-11 w-11 items-center justify-center rounded-full text-text-secondary transition-colors hover:bg-border-light"
                            >
                              <MobileIcon name="edit_note" className="h-5 w-5" />
                            </button>
                          )}
                          {canDeleteCommercial && (
                            <button
                              type="button"
                              aria-label={`Eliminar partida ${itemTitle(item)}`}
                              onClick={() => setDeletingItem(item)}
                              className="flex h-11 w-11 items-center justify-center rounded-full text-error transition-colors hover:bg-error-light"
                            >
                              <MobileIcon name="cancel" className="h-5 w-5" />
                            </button>
                          )}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {canAdjust && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-text-primary">Ajuste global</h3>
                <div role="radiogroup" aria-label="Tipo de ajuste" className="flex gap-1 rounded-full bg-bg-tertiary p-1">
                  {(
                    [
                      { value: 'discount', label: 'Descuento' },
                      { value: 'surcharge', label: 'Recargo' },
                    ] as const
                  ).map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      role="radio"
                      aria-checked={adjustmentType === option.value}
                      onClick={() => setAdjustmentType(option.value)}
                      className={`flex h-11 min-h-[44px] flex-1 items-center justify-center rounded-full text-sm font-semibold ${
                        adjustmentType === option.value
                          ? 'bg-white text-text-primary shadow-card'
                          : 'text-text-secondary'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                <div role="radiogroup" aria-label="Forma del ajuste" className="flex gap-1 rounded-full bg-bg-tertiary p-1">
                  {(
                    [
                      { value: 'amount', label: 'Monto fijo' },
                      { value: 'percent', label: 'Porcentaje' },
                    ] as const
                  ).map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      role="radio"
                      aria-checked={adjustmentMode === option.value}
                      onClick={() => setAdjustmentMode(option.value)}
                      className={`flex h-11 min-h-[44px] flex-1 items-center justify-center rounded-full text-sm font-semibold ${
                        adjustmentMode === option.value
                          ? 'bg-white text-text-primary shadow-card'
                          : 'text-text-secondary'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                <MobileInput
                  label={adjustmentMode === 'percent' ? 'Porcentaje (0 a 100)' : 'Monto'}
                  type="number"
                  inputMode="decimal"
                  staticLabel
                  isRequired
                  value={adjustmentValue}
                  onChange={(e) => setAdjustmentValue(e.target.value)}
                  icon={<MobileIcon name="payments" className="h-5 w-5" />}
                />
                <MobileInput
                  label="Justificación"
                  placeholder="Mínimo 5 caracteres..."
                  staticLabel
                  isRequired
                  value={adjustmentJustification}
                  onChange={(e) => setAdjustmentJustification(e.target.value)}
                  icon={<MobileIcon name="edit_note" className="h-5 w-5" />}
                />
                {adjustmentError && <p className="break-words text-sm text-error">{adjustmentError}</p>}
                {simulationPreview && (
                  <p role="status" className="break-words rounded-xl bg-info-light px-4 py-3 text-sm text-info-dark">
                    {simulationPreview}
                  </p>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <MobileButton
                    variant="secondary"
                    size="md"
                    loading={totals.simulate.isPending}
                    onClick={() => void handleSimulateAdjustment()}
                  >
                    Simular
                  </MobileButton>
                  <MobileButton
                    variant="primary"
                    size="md"
                    loading={adjustments.applyAdjustment.isPending}
                    onClick={() => void handleApplyAdjustment()}
                  >
                    Aplicar
                  </MobileButton>
                </div>
                {adjustmentItems.length > 0 && (
                  <ul className="space-y-2 pt-1">
                    {adjustmentItems.map((adjustment) => (
                      <li
                        key={adjustment.id}
                        className="flex min-h-[52px] items-center gap-2 rounded-xl bg-bg-tertiary px-4 py-2"
                      >
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold text-text-primary">
                            {adjustment.adjustment_type === 'surcharge' ? 'Recargo' : 'Descuento'}
                          </span>
                          <span className="block break-words text-xs text-text-secondary line-clamp-2">
                            {adjustment.adjustment_justification ?? 'Sin justificación'}
                          </span>
                        </span>
                        <button
                          type="button"
                          aria-label={`Quitar ajuste ${adjustment.adjustment_type ?? ''}`}
                          onClick={() => setClearingItem(adjustment)}
                          className="flex h-11 w-11 items-center justify-center rounded-full text-error transition-colors hover:bg-error-light"
                        >
                          <MobileIcon name="cancel" className="h-5 w-5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </SectionCard>
        )}

        {canViewSnapshots && (
          <SectionCard
            title={`Fotos (${snapshots.length})`}
            subtitle="Snapshots del presupuesto, la más reciente primero."
          >
            {snapshotsQuery.isPending && <CardListSkeleton count={2} />}
            {snapshotsQuery.isError && (
              <EmptyState
                icon="error"
                title="No se pudieron cargar las fotos"
                description="Verifica tu conexión e intenta de nuevo."
                actionLabel="Reintentar"
                onAction={() => void snapshotsQuery.refetch()}
              />
            )}
            {!snapshotsQuery.isPending && !snapshotsQuery.isError && snapshots.length === 0 && (
              <div className="rounded-xl bg-bg-tertiary p-4 text-center">
                <p className="truncate text-sm font-semibold text-text-primary">Sin fotos</p>
                <p className="text-sm text-text-secondary">Aún no hay snapshots de este presupuesto.</p>
              </div>
            )}
            {!snapshotsQuery.isPending && !snapshotsQuery.isError && snapshots.length > 0 && (
              <ul className="space-y-2">
                {snapshots.map((foto) => {
                  const isCurrentBudget = foto.budget_id === budget.id;
                  const fotoLabel = isCurrentBudget
                    ? `foto ${foto.snapshot_number} · ${budget.status} · ${formatDate(foto.created_at)}`
                    : `foto ${foto.snapshot_number} · ${formatDate(foto.created_at)}`;
                  return (
                    <li
                      key={foto.id}
                      className="flex min-h-[52px] items-center gap-2 rounded-xl bg-bg-tertiary px-4 py-2"
                    >
                      <MobileIcon name="receipt_long" className="h-5 w-5 shrink-0 text-text-tertiary" />
                      <span className="min-w-0 flex-1">
                        <span title={fotoLabel} className="block truncate text-sm font-semibold text-text-primary">
                          {fotoLabel}
                        </span>
                        {foto.comments && (
                          <span className="block break-words text-xs text-text-secondary line-clamp-2">
                            {foto.comments}
                          </span>
                        )}
                      </span>
                      {!isCurrentBudget && foto.budget_id && (
                        <Link
                          to={`/m/budgets/${foto.budget_id}`}
                          className="flex min-h-[44px] shrink-0 items-center rounded-full bg-secondary-light px-4 text-sm font-semibold text-secondary-dark-text"
                        >
                          Ver ficha
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </SectionCard>
        )}

        <SectionCard title={`Adjuntos (${attachments.length})`}>
          {attachmentsQuery.isPending && <CardListSkeleton count={2} />}
          {attachmentsQuery.isError && (
            <EmptyState
              icon="error"
              title="No se pudieron cargar los adjuntos"
              description="Verifica tu conexión e intenta de nuevo."
              actionLabel="Reintentar"
              onAction={() => void attachmentsQuery.refetch()}
            />
          )}
          {!attachmentsQuery.isPending && !attachmentsQuery.isError && attachments.length === 0 && (
            <div className="mb-3 rounded-xl bg-bg-tertiary p-4 text-center">
              <p className="truncate text-sm font-semibold text-text-primary">Sin adjuntos</p>
              <p className="text-sm text-text-secondary">Agrega fotos o archivos del presupuesto.</p>
            </div>
          )}
          {!attachmentsQuery.isPending && !attachmentsQuery.isError && attachments.length > 0 && (
            <ul className="mb-3 space-y-2">
              {attachments.map((attachment) => (
                <li
                  key={attachment.id}
                  className="flex min-h-[52px] items-center gap-2 rounded-xl bg-bg-tertiary px-4 py-2"
                >
                  <MobileIcon name="folder_open" className="h-5 w-5 shrink-0 text-text-tertiary" />
                  <span className="min-w-0 flex-1">
                    <span title={attachment.file_name} className="block truncate text-sm font-semibold text-text-primary">
                      {attachment.file_name}
                    </span>
                    {attachment.description && (
                      <span className="block break-words text-xs text-text-secondary line-clamp-2">
                        {attachment.description}
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <FileCapture onFiles={(files) => void handleFiles(files)} disabled={uploadAttachmentsMutation.isPending} />
          {uploadAttachmentsMutation.isPending && (
            <div className="mt-2 flex items-center gap-2 rounded-2xl bg-info-light px-4 py-3">
              <MobileIcon name="sync" className="h-5 w-5 shrink-0 animate-spin text-info-dark" />
              <p className="min-w-0 flex-1 truncate text-sm text-info-dark">Subiendo adjuntos...</p>
            </div>
          )}
          {uploadError && (
            <div className="mt-2 flex items-center gap-2 rounded-2xl bg-error-light px-4 py-3">
              <MobileIcon name="error" className="h-5 w-5 shrink-0 text-error" />
              <p className="min-w-0 flex-1 break-words text-sm text-error">{uploadError}</p>
            </div>
          )}
        </SectionCard>
      </div>

      {deletingItem && (
        <ConfirmDialog
          title="Eliminar partida"
          message={`¿Confirmas eliminar "${itemTitle(deletingItem)}"? Esta acción no se puede deshacer.`}
          confirmLabel="Eliminar"
          danger
          busy={deleteItemMutation.isPending}
          onConfirm={() => void handleConfirmDelete()}
          onCancel={() => setDeletingItem(null)}
        />
      )}

      {clearingItem && (
        <ConfirmDialog
          title="Quitar ajuste"
          message="¿Confirmas quitar este ajuste del precio de venta?"
          confirmLabel="Quitar"
          danger
          busy={adjustments.clearAdjustment.isPending}
          onConfirm={() => void handleConfirmClearAdjustment()}
          onCancel={() => setClearingItem(null)}
        />
      )}
    </MobileLayout>
  );
}
